<template>
  <!-- .carousel-viewport, NOT overflow-x-hidden: 'hidden' on only one axis
       forces the browser to compute the other axis (overflow-y, left at its
       default 'visible') as 'auto' instead — a CSS Overflow spec rule most
       people never hit. That silently turned this wrapper into a vertical clip
       container sized to its own laid-out height, which cuts off the centre
       card's box-shadow bleed below the mask viewport (shadows don't count
       toward layout height, so the wrapper never grew to make room for one).
       'clip' doesn't pair with 'visible' that way, so overflow-y stays
       genuinely visible and the mask div below is what does the clipping. See
       the class's own rule below for why it isn't the overflow-x-clip Tailwind
       utility. -->
  <div class="w-full carousel-viewport select-none">
    <!-- 3D Scene -->
    <!-- Outer viewport: clipping + edge-fade mask only (no perspective). Keeping
         these grouping properties off the perspective root stops mobile WebViews
         from rasterizing the 3D subtree to 2D (which flattens the side cards). -->
    <div class="relative mx-auto" :style="maskStyle">
      <!-- Inner stage: the clean 3D root — perspective only, no mask/clip/overflow. -->
      <div
        ref="stage"
        class="w-full h-full"
        :style="stageStyle"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointercancel="onPointerCancel"
      >
        <div
          class="w-full h-full flex items-center justify-center"
          style="transform-style: preserve-3d"
        >
          <div :style="cylinderStyle">
            <!-- The whole card face is tappable: the centre card acts like its
                 CTA (navigate / locked toast), a side card recentres itself.
                 Without this, only the small CTA button navigated and a tap on
                 the card body silently did nothing — which reads as the app
                 hanging. onCardTap ignores the click that follows a swipe. -->
            <div
              v-for="(card, i) in cards"
              :key="card.id"
              class="absolute top-0 left-0 rounded-2xl overflow-hidden cursor-pointer"
              :style="getCardStyle(i)"
              @click="onCardTap(i, card)"
            >
              <!-- Living background on the visible cards (center + neighbours).
                   The focused card plays the video; side cards show a static
                   first-frame image. Sits behind the content (z-0), clipped by
                   the rounded corners. -->
              <CardVideoBackground
                v-if="showsVideo(card, i)"
                :src="card.backgroundAnimation"
                :poster="card.backgroundPoster"
                :playing="i === currentIndex"
                class="z-0"
              />

              <div
                class="relative z-10 flex h-full w-full flex-col items-center justify-center p-5"
              >
                <!-- Soft lock icon for locked non-center cards -->
                <div
                  v-if="card.locked && i !== currentIndex && !card.showLockedBadge"
                  class="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center"
                  :style="{ backgroundColor: softIconBg }"
                >
                  <svg
                    class="w-3.5 h-3.5"
                    :style="{ color: softIconColor }"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>

                <!-- Lesson number + progress pill (top-right). Replaces the old
                     top-left status badges. Renders on every visible card,
                     including over a video background whose own text overlay is
                     hidden below. Suppressed on locked cards, which show the lock
                     icon above instead, and on cards with no lesson number
                     (e.g. the guide intro card). -->
                <span
                  v-if="!card.locked && lessonNoLabel(card)"
                  class="absolute top-3 right-3 z-[1] inline-flex items-center rounded-full px-2.5 py-1 text-[11px] tablet:text-caption-md font-bold uppercase tracking-[0.04em]"
                  :style="lessonNoStyle(i)"
                  >{{ lessonNoLabel(card) }}</span
                >

                <!-- The card video carries its own lesson number + title, so hide
                     the text overlay on any card rendering it (the playing center
                     and the paused neighbours alike). Cards with no video — or
                     off-screen — keep their labels. -->
                <template v-if="!showsVideo(card, i)">
                  <span
                    class="mb-3 text-balance text-center text-[0.625rem] font-semibold uppercase tracking-[0.15em] tablet:text-caption-md"
                    :style="{ color: card.cardTextColor || cardTextColor, opacity: 0.8 }"
                    >{{ card.label }}</span
                  >

                  <h3
                    class="text-[1.375rem] tablet:text-heading-lg font-extrabold text-center leading-tight whitespace-pre-line"
                    :style="{ color: card.cardTextColor || cardTextColor }"
                  >
                    {{ card.title }}
                  </h3>
                </template>

                <!-- Center card CTA. With a video background the number/title are
                     hidden, so push the button to the bottom (mt-auto) instead of
                     sitting centered under the (now absent) title. -->
                <button
                  v-if="i === currentIndex"
                  class="block max-w-full shrink-0 text-balance rounded-[2rem] px-5 py-2.5 text-center text-sm font-semibold leading-snug transition-transform active:scale-95 tablet:px-7 tablet:text-base"
                  :class="card.backgroundAnimation ? 'mt-auto' : 'mt-5'"
                  :style="getCtaStyle(card)"
                  @click.stop="handleCTA(card)"
                >
                  <template v-if="card.locked">Sign up</template>
                  <template v-else-if="card.status === 'complete'">
                    <svg
                      class="cta-glyph cta-glyph--lead"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    {{ card.buttonLabel || 'Revisit Lesson' }}
                  </template>
                  <template v-else>
                    {{ card.buttonLabel || buttonLabel }}
                    <svg
                      class="cta-glyph"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </template>
                </button>

                <!-- Non-center pill label -->
                <span
                  v-else
                  class="mt-5 px-4 py-1.5 rounded-full text-[0.6875rem] tablet:text-caption-lg font-semibold tracking-wide"
                  :style="{ backgroundColor: pillBg, color: pillText }"
                >
                  {{ card.locked ? 'Locked' : 'Preview' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dot indicators -->
    <div class="flex items-center justify-center gap-2 mt-2">
      <button
        v-for="(_, i) in cards"
        :key="i"
        class="rounded-full"
        :style="getDotStyle(i)"
        @click="$emit('index-change', i)"
      />
    </div>
  </div>
</template>

<script>
import CardVideoBackground from './CardVideoBackground.vue';
import { fontScaleState } from '@/composables/useFontScale';

// Swipe-gesture tuning — the knobs to turn if the carousel feels too rigid or
// too lax. See the pointer handlers below for how each is applied.
const SWIPE = {
  // How diagonal a swipe may be and still turn the card (vs. scroll the page),
  // as the minimum |dx| / |dy| ratio. 0.6 ≈ 59° off horizontal. LOWER tolerates
  // steeper angles but steals more vertical scroll; 1.0 is a strict 45°.
  ANGLE_RATIO: 0.6,
  // Travel (px) before an axis is locked — tap-slop, not a feel knob.
  ARM_THRESHOLD: 8,
  // Horizontal travel (px) for a slow drag to turn the card.
  DISTANCE: 40,
  // A quick flick turns the card below DISTANCE: min travel (px) within MS.
  FLICK_DISTANCE: 20,
  FLICK_MS: 250,
};

export default {
  name: 'CoursesCarousel',
  components: { CardVideoBackground },
  props: {
    cards: {
      type: Array,
      default: () => [],
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    cardColor: {
      type: String,
      default: '#EBE7E0',
    },
    cardTextColor: {
      type: String,
      default: '#1D212B',
    },
    buttonLabel: {
      type: String,
      default: 'Begin Lesson',
    },
    isDark: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['index-change', 'navigate', 'locked-tap'],
  data() {
    return {
      cardW: 220,
      cardH: 320,
      // Pending scheduleDims frame, so a burst of text-size changes measures once.
      dimsFrame: 0,
      dragStartX: null,
      dragStartY: null,
      dragStartTime: 0,
      boundPointerUp: null,
      // Axis lock decided once per gesture on the first armed move:
      // 'x' → we own it (swipe), 'y' → the browser scrolls the page.
      dragAxis: null,
      // Live horizontal offset (px) while dragAxis === 'x'; drives the cards to
      // follow the finger and is read again on release to decide the page turn.
      dragX: 0,
      // Set on release when the pointer travelled beyond tap-slop; the card's
      // click handler reads it to ignore the click a drag still fires.
      pointerMoved: false,
    };
  },
  computed: {
    sideOffset() {
      return this.cardW * 0.95;
    },
    // Outer viewport: clips the bottom (shadow bleed) and fades the left/right
    // edges. These grouping properties are kept OFF the perspective root so the
    // WebView doesn't rasterize the 3D card subtree into a flat 2D layer.
    maskStyle() {
      return {
        height: `${this.cardH + 80}px`,
        clipPath: 'inset(0 0 -40px 0)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent 0%, black 7%, black 93%, transparent 100%)',
        maskImage:
          'linear-gradient(to right, transparent 0%, black 7%, black 93%, transparent 100%)',
      };
    },
    // Inner stage: the clean 3D root — perspective only, no mask/clip/overflow.
    stageStyle() {
      return {
        perspective: '1200px',
        perspectiveOrigin: '50% 45%',
        // Grant native vertical scroll. Horizontal (and angled) gestures are
        // locked to the swipe in onPointerMove and held by onTouchMove, so the
        // browser only scrolls when the drag is steep enough to lock 'y'.
        touchAction: 'pan-y',
      };
    },
    cylinderStyle() {
      return {
        transformStyle: 'preserve-3d',
        width: `${this.cardW}px`,
        height: `${this.cardH}px`,
        position: 'relative',
      };
    },
    softIconBg() {
      return this.isDark ? 'rgba(247,243,236,0.08)' : 'rgba(29,33,43,0.08)';
    },
    softIconColor() {
      return this.isDark ? 'rgba(247,243,236,0.55)' : 'rgba(29,33,43,0.55)';
    },
    pillBg() {
      return this.isDark ? 'rgba(247,243,236,0.08)' : 'rgba(29,33,43,0.08)';
    },
    pillText() {
      return this.isDark ? 'rgba(247,243,236,0.7)' : 'rgba(29,33,43,0.7)';
    },

    // Module-level ref read from a computed, so the watcher below still fires on
    // a scale change without a setup() entry point (AGENTS.md forbids one).
    fontScale() {
      return fontScaleState.scale.value;
    },
  },
  mounted() {
    this.updateDims();
    window.addEventListener('resize', this.updateDims);
    // preventDefault on pointermove does NOT stop scrolling — only a non-passive
    // touchmove does. This listener exists solely to hold a horizontal drag
    // (dragAxis === 'x') so the browser doesn't scroll the page underneath it.
    // touch-action: pan-y still lets vertical gestures scroll natively.
    this.$refs.stage?.addEventListener('touchmove', this.onTouchMove, { passive: false });
  },
  beforeUnmount() {
    if (this.dimsFrame) cancelAnimationFrame(this.dimsFrame);
    window.removeEventListener('resize', this.updateDims);
    this.$refs.stage?.removeEventListener('touchmove', this.onTouchMove);
    this.removePointerListeners();
  },
  watch: {
    // updateDims only ran on mount and on resize, and a text-size change is
    // neither — so the card kept the width it was born with while the type grew
    // past it, stacking the title and inflating the CTA. This is the trigger
    // that was missing; the harness slider changes the scale live, and so does
    // the OS setting on resume.
    fontScale() {
      this.scheduleDims();
    },
  },
  methods: {
    // updateDims measures the stage and then writes cardW/cardH, so calling it
    // straight from the watcher forced a layout in the same tick as the font-size
    // change that triggered it — read, write, read, twice for every step of the
    // text-size slider, which is what made a drag flicker rather than resize.
    // One measure per frame, after the browser has laid the new size out.
    scheduleDims() {
      if (typeof requestAnimationFrame !== 'function') {
        this.updateDims();
        return;
      }
      if (this.dimsFrame) return;
      this.dimsFrame = requestAnimationFrame(() => {
        this.dimsFrame = 0;
        this.updateDims();
      });
    },
    updateDims() {
      // Measure our own box rather than the viewport. The Courses pages now cap
      // content to a reading width, so window.innerWidth over-reports on tablet
      // and would size cards against space the carousel doesn't actually have.
      const stageW = this.$el?.clientWidth || window.innerWidth;
      // Whether we're on a tablet is still a viewport question, not a container
      // one — the container is capped, so it never reaches the tablet breakpoint
      // on its own. 744 matches the `tablet` Tailwind breakpoint (iPad mini
      // portrait), NOT md's 768, which would skip the smallest iPad.
      const isTabletViewport = window.innerWidth >= 744;
      // Phones keep the original 55% / 260 sizing exactly.
      //
      // Tablets get a bigger card but a *smaller* width fraction, because the
      // whole stage is about 2.67x cardW: sideOffset is cardW * 0.95, and the
      // neighbour adds its rotated half-width (~0.39x) on each side. At 0.55 the
      // neighbours ran past the viewport and were clipped mid-word. 0.36 keeps
      // all three cards on screen from 744pt (iPad mini portrait) upward:
      //   744 -> 268 (span ~715)   820 -> 295 (span ~788)   1032+ -> 300 (span ~801)
      const fontScale = this.fontScale || 1;
      // Both axes track the font scale, and width is the one that matters.
      //
      // The stage is a fixed three-card peek, so on a card that only grows
      // downward a larger text size has nowhere to go: the title stacks a word
      // per line and the CTA wraps until the rounded-full pill inflates into a
      // blob with the arrow stranded on its own row. Widening the centre card
      // gives the text somewhere to go — the neighbours simply peek less, which
      // is the right thing to trade away.
      //
      // The fraction is capped so the card can never swallow the whole stage;
      // at scale 1 both terms collapse to the original 55% / 260 (0.36 / 300 on
      // tablet) and nothing about the default layout changes.
      const baseFraction = isTabletViewport ? 0.36 : 0.55;
      const maxFraction = isTabletViewport ? 0.52 : 0.82;
      const widthFraction = Math.min(baseFraction * fontScale, maxFraction);
      this.cardW = Math.min(stageW * widthFraction, (isTabletViewport ? 300 : 260) * fontScale);
      this.cardH = Math.min(this.cardW * 1.45, (isTabletViewport ? 440 : 380) * fontScale);
    },
    // Top-right pill text: the structured lesson number plus a progress suffix.
    // Empty when the card has no lesson number (the guide intro card) so the pill
    // is hidden.
    lessonNoLabel(card) {
      if (!card.lessonNumber) return '';
      const suffix = { complete: ' · Complete', inProgress: ' · In Progress' }[card.status] || '';
      return `Lesson ${card.lessonNumber}${suffix}`;
    },
    lessonNoStyle(i) {
      return {
        // Dark translucent chip with light ink — legible on the warm card tones
        // and over a looping video frame alike. Matches the badge-over-video tint
        // this component already used.
        backgroundColor: 'rgba(29,33,43,0.5)',
        color: '#F6F4EE',
        // The focused card scales up (1.04); nudge the pill back so it holds the
        // same visual size as on the side cards. Matches the design's 0.9615.
        transform: i === this.currentIndex ? 'scale(0.9615)' : 'none',
        transformOrigin: 'top right',
      };
    },
    getCtaStyle(card) {
      // Focused card with a looping video background: a solid white pill with
      // dark ink so the CTA stays legible over any video frame.
      if (card.backgroundAnimation) {
        return { backgroundColor: '#FFFFFF', color: '#1D212B' };
      }
      const bgColor = card.cardTextColor || this.cardTextColor;
      // Cards with an explicit (dark) cardTextColor → always light CTA text.
      // Cards using the global prop (which flips in dark mode) → invert accordingly.
      const textColor = card.cardTextColor ? '#F6F4EE' : this.isDark ? '#1D1916' : '#F6F4EE';
      return { backgroundColor: bgColor, color: textColor };
    },
    // Shortest signed ring offset from the focused card, wrapping around the ends
    // so the last card neighbours the first. Negative = left of centre, positive =
    // right. getCardStyle needs the sign (slide/rotate direction); cardDistance
    // takes the magnitude.
    cardOffset(i) {
      const len = this.cards.length;
      const rawOffset = i - this.currentIndex;
      return rawOffset > len / 2
        ? rawOffset - len
        : rawOffset < -len / 2
          ? rawOffset + len
          : rawOffset;
    },
    // 0 = center, 1 = immediate neighbour, higher = off-screen.
    cardDistance(i) {
      return Math.abs(this.cardOffset(i));
    },
    // A card renders its media background (video on the centre, still <img> on
    // the sides) when it has one and is within one card of the visible window.
    // The extra step (<= 2, not <= 1) is off-screen at opacity 0, so it simply
    // preloads/decodes the next still before it slides in — no base-colour flash
    // on the card entering in the scroll direction. Single source for the
    // invariant: it gates the media element AND suppresses the text overlay the
    // artwork carries.
    showsVideo(card, i) {
      return !!card.backgroundAnimation && this.cardDistance(i) <= 2;
    },
    getCardStyle(i) {
      const card = this.cards[i];
      const isCenter = i === this.currentIndex;
      const offset = this.cardOffset(i);
      const isNeighbor = Math.abs(offset) === 1;
      const opacity = isCenter ? 1 : isNeighbor ? 0.65 : 0;
      // While a horizontal drag is live, the whole cylinder tracks the finger so
      // the user sees the cards move and can gauge how far a page turn needs to go.
      const dragging = this.dragAxis === 'x';
      const baseX = isCenter ? 0 : offset * this.sideOffset;
      const translateX = baseX + (dragging ? this.dragX : 0);
      // Side cards slope AWAY from the focused card: the outer edge recedes so
      // each neighbor angles down-and-back, opening a gap next to the center card.
      // A gentler angle keeps the neighbors upright and wide (matches Lovable).
      const rotateY = isCenter ? 0 : offset < 0 ? -38 : 38;
      const translateZ = isCenter ? 28 : -70;
      const scale = isCenter ? 1.04 : 0.98;
      const darkGlow =
        this.isDark && card.darkGlow && isCenter ? ', 0 0 28px 8px rgba(255,255,255,0.14)' : '';
      const shadow = isCenter
        ? `0 20px 40px -12px rgba(29,33,43,0.28), 0 8px 16px -8px rgba(29,33,43,0.18)${darkGlow}`
        : '0 6px 14px -8px rgba(29,33,43,0.18)';
      return {
        width: `${this.cardW}px`,
        height: `${this.cardH}px`,
        backgroundColor: this.cards[i].cardColor || this.cardColor,
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        opacity,
        pointerEvents: isCenter ? 'auto' : 'none',
        boxShadow: shadow,
        filter: isCenter ? 'none' : 'saturate(0.9)',
        zIndex: isCenter ? 2 : 1,
        transform: `translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotateY}deg) scale(${scale})`,
        // No snap animation while the finger is down — the cards must track it
        // 1:1. On release dragAxis clears and the spring transition animates back.
        transition: dragging
          ? 'none'
          : 'transform 0.35s cubic-bezier(0.34, 1.4, 0.64, 1), opacity 0.3s ease',
      };
    },
    getDotStyle(i) {
      const isActive = i === this.currentIndex;
      const activeDot = this.isDark ? '#F7F3EC' : '#2B2A28';
      const inactiveDot = this.isDark ? '#67635F' : '#DAD3C8';
      return {
        width: isActive ? '24px' : '8px',
        height: '8px',
        backgroundColor: isActive ? activeDot : inactiveDot,
        transition: 'all 0.3s ease',
      };
    },
    // Single Pointer Events path covers mouse + touch + pen (mirrors
    // useVinylGestures). `touch-action: pan-y` grants native vertical scroll; we
    // lock an axis on the first armed move and, once it's horizontal, follow the
    // finger and hold the gesture (onTouchMove) so a swipe that STARTS at an
    // angle isn't handed to the scroller and cancelled before release.
    onPointerDown(e) {
      // No preventDefault — keeps the center-card CTA and dot buttons clickable.
      this.pointerMoved = false;
      this.dragAxis = null;
      this.dragX = 0;
      this.dragStartX = e.clientX;
      this.dragStartY = e.clientY;
      this.dragStartTime = e.timeStamp;
      this.boundPointerUp = this.onPointerUp.bind(this);
      window.addEventListener('pointerup', this.boundPointerUp);
    },
    onPointerMove(e) {
      if (this.dragStartX === null || this.dragAxis === 'y') return;
      const dx = e.clientX - this.dragStartX;
      const dy = e.clientY - this.dragStartY;
      // Arm only after clearing tap-slop, so a still finger never picks an axis.
      if (this.dragAxis === null) {
        if (Math.abs(dx) < SWIPE.ARM_THRESHOLD && Math.abs(dy) < SWIPE.ARM_THRESHOLD) return;
        // Horizontal bias: claim the swipe even when the gesture is angled, up to
        // SWIPE.ANGLE_RATIO off horizontal. Steeper than that is left to scroll.
        this.dragAxis = Math.abs(dx) >= Math.abs(dy) * SWIPE.ANGLE_RATIO ? 'x' : 'y';
      }
      if (this.dragAxis === 'x') this.dragX = dx;
    },
    // Non-passive: the only reliable lever to stop the page scrolling under a
    // horizontal drag. Fires alongside pointermove; it never mutates drag state.
    onTouchMove(e) {
      if (this.dragAxis === 'x' && e.cancelable) e.preventDefault();
    },
    onPointerUp(e) {
      this.removePointerListeners();
      if (this.dragStartX === null) return;
      const deltaX = e.clientX - this.dragStartX;
      const deltaY = e.clientY - this.dragStartY;
      const elapsed = e.timeStamp - this.dragStartTime;
      const axis = this.dragAxis;
      this.dragStartX = null;
      this.dragStartY = null;
      this.dragAxis = null;
      this.dragX = 0;
      // A drag is not a tap: the browser still fires `click` when down and up
      // both land on the (large) card, so onCardTap checks this flag. 10px
      // matches the usual tap-slop rather than the swipe threshold.
      this.pointerMoved = Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10;
      // Only a horizontally-locked gesture turns the page.
      if (axis !== 'x') return;
      // Distance OR a quick flick: a short, fast angled swipe still counts.
      const isFlick = Math.abs(deltaX) > SWIPE.FLICK_DISTANCE && elapsed < SWIPE.FLICK_MS;
      if (Math.abs(deltaX) < SWIPE.DISTANCE && !isFlick) return;
      const len = this.cards.length;
      if (len === 0) return;
      if (deltaX < 0) {
        this.$emit('index-change', (this.currentIndex + 1) % len);
      } else {
        this.$emit('index-change', (this.currentIndex - 1 + len) % len);
      }
    },
    onPointerCancel() {
      // The browser claimed the gesture (vertical pan) — never treat it as a tap.
      this.pointerMoved = true;
      this.dragStartX = null;
      this.dragStartY = null;
      this.dragAxis = null;
      this.dragX = 0;
      this.removePointerListeners();
    },
    removePointerListeners() {
      if (this.boundPointerUp) window.removeEventListener('pointerup', this.boundPointerUp);
      this.boundPointerUp = null;
    },
    handleCTA(card) {
      if (card.locked) {
        this.$emit('locked-tap', card);
      } else {
        this.$emit('navigate', card.path);
      }
    },
    onCardTap(i, card) {
      if (this.pointerMoved) return;
      if (i === this.currentIndex) {
        this.handleCTA(card);
      } else {
        this.$emit('index-change', i);
      }
    },
  },
};
</script>

<style scoped>
/* `clip` (not `hidden`) so this axis doesn't force overflow-y to 'auto' — see
   the template comment on this element. `clip` support starts at Safari 16;
   this app's IPHONEOS_DEPLOYMENT_TARGET is 13.0, so iOS 13-15 WebViews need a
   fallback. A CSS custom property can't hold two values, so this can't be a
   Tailwind arbitrary-value utility — it needs the redeclaration trick: an
   engine that doesn't recognise `clip` treats the second line as an invalid
   value for a property it already has a value for and keeps the first
   (`hidden`, this component's pre-existing, un-regressed behaviour); one that
   does overrides it with `clip`. Order matters — `clip` must be second. */
.carousel-viewport {
  overflow-x: hidden;
  overflow-x: clip;
}

/* CTA glyph — sized from the shared icon tokens (src/theme/control-sizing.css)
   so it steps up on tablet with the rest of the icon system rather than being
   frozen at a utility size. */
/* Inline, not a flex child. As a flex item with wrap the arrow took a row of
   its own the moment the label needed two lines, which — under a rounded-full
   pill — is what turned the CTA into a blob. Inline, it stays attached to the
   last word and the button wraps like the text it is. */
.cta-glyph {
  display: inline-block;
  width: var(--icon-sm);
  height: var(--icon-sm);
  flex-shrink: 0;
  vertical-align: -0.15em;
  margin-inline-start: 0.5em;
}
/* Leading glyph (the completed state) sits before the label instead. */
.cta-glyph--lead {
  margin-inline-start: 0;
  margin-inline-end: 0.5em;
}
</style>
